import React from 'react';
import { Renderer, RendererProps } from '../factory';
import { ResizeEvent, Schema } from '../types';
import pick from 'lodash/pick';
import sum from 'lodash/sum';
import {
  BaseSchema,
  SchemaClassName,
  SchemaCollection,
  SchemaObject
} from '../Schema';
import { FormSchemaHorizontal } from './Form/index';
import { domUtils, isMobile, ucFirst } from '../utils/helper';

import { Icon } from '../components/icons';
import { getStyleNumber } from '../utils/dom';
import { CaretLeftOutlined, CaretRightOutlined, DoubleLeftOutlined, DoubleRightOutlined } from '@ant-design/icons';
export const ColProps = ['lg', 'md', 'sm', 'xs'];

export type GridColumnObject = {
  /**
   * 极小屏（<768px）时宽度占比
   */
  xs?: number | 'auto';

  /**
   * 小屏时（>=768px）宽度占比
   */
  sm?: number | 'auto';

  /**
   * 中屏时(>=992px)宽度占比
   */
  md?: number | 'auto';

  /**
   * 大屏时(>=1200px)宽度占比
   */
  lg?: number | 'auto';

  /**
   * 垂直对齐方式
   */
  valign?: 'top' | 'middle' | 'bottom' | 'between';

  /**
   * 配置子表单项默认的展示方式。
   */
  mode?: 'normal' | 'inline' | 'horizontal';

  /**
   * 如果是水平排版，这个属性可以细化水平排版的左右宽度占比。
   */
  horizontal?: FormSchemaHorizontal;

  body?: SchemaCollection;

  // 占据的行数
  rowKey?: number
  // 总行数
  rowCount?: number
  // 是否固定高度
  fixedHeight?: boolean
  /** 固定高度多少 不配置将占满剩余容器 */
  height?: number
  /**
   * 列类名
   */
  columnClassName?: SchemaClassName;
  // Jay
  style: {
    [key: string]: string | number;
  }
  /**
   * 特殊处理移动端需要水平布局的情况
   */
  mobLayout?: boolean
  /** 是否固定在顶部  0 不固定 1强制固定 2 显示配置，用户自己决定是否固定 */
  isFixed?: number
};

export type GridColumn = GridColumnObject;
export type ColumnNode = GridColumn;
export interface ColumnArray extends Array<ColumnNode> { }

/**
 * Grid 格子布局渲染器。
 * 文档：https://baidu.gitee.io/amis/docs/components/grid
 */
export interface GridSchema extends BaseSchema {
  /**
   * 指定为 Grid 格子布局渲染器。
   */
  type: 'grid';

  /**
   * 列集合
   */
  columns: Array<GridColumn>;

  /**
   * 水平间距
   */
  gap?: 'xs' | 'sm' | 'base' | 'none' | 'md' | 'lg';

  /**
   * 垂直对齐方式
   */
  valign?: 'top' | 'middle' | 'bottom' | 'between';

  /**
   * 水平对齐方式
   */
  align?: 'left' | 'right' | 'between' | 'center';

  /**
   * 是否可拖动
   */
  scroll?: boolean
  /**
  * 是否上下展示
  */
  distance?: boolean
  /** 与distance一起用，功能为 是否上下展示时，不去计算高度  0: 计算 1：不计算  */
  autoHeight?: number;
  /** 配置是否sticky: 0 不配置 1 强制配置 2 显示用户自定义按钮 */
  isFixed?: number;
}

export interface GridProps
  extends RendererProps,
  Omit<GridSchema, 'type' | 'className' | 'columnClassName'> {
  itemRender?: (item: any, length: number, props: any) => JSX.Element;
  scroll?: boolean;
  className?: string;
  distance?: boolean;
  autoHeight?: number;
}

function fromBsClass(cn: string) {
  if (typeof cn === 'string' && cn) {
    return cn.replace(
      /\bcol-(xs|sm|md|lg)-(\d+)\b/g,
      (_, bp, size) => `Grid-col--${bp}${size}`
    );
  }

  return cn;
}

function copProps2Class(props: any): string {
  const cns: Array<string> = [];
  const modifiers = ColProps;

  modifiers.forEach(
    modifier =>
      props &&
      props[modifier] &&
      cns.push(`Grid-col--${modifier}${ucFirst(props[modifier])}`)
  );
  cns.length || cns.push('Grid-col--md');
  return cns.join(' ');
}

export default class Grid<T> extends React.Component<GridProps & T, {
  x: number,
  scrollRefLeft: number,
  scrollRefTop: number,
  mds: string[],
  isDrag: boolean,
  distanceStyle: string,
  navVisible: boolean,
  hasFixedHeight: boolean,
  fixedHeightArr: number[],
  isFixed?: boolean,
  // isHovered: boolean,
  // showLeft?: boolean
}> {
  static propsList: Array<string> = ['columns'];

  // static defaultProps = {
  //   scroll: true
  // }
  showCollpase: boolean
  pageContentId: string = '';
  scrollParent: any = React.createRef()
  scrollRef: any = React.createRef()
  scrollRefChildren: any = React.createRef()
  gridRef: any = React.createRef();
  resizeObserver: any;
  constructor(props: GridProps & T) {
    super(props);
    const columnsArr = (Array.isArray(this.props.columns) ? this.props.columns.map((column, key) => {
      const hasFixedItem = (column.body as any[])?.find?.((_) => _.height && _.fixedHeight);
      return hasFixedItem
    }).filter(Boolean) : []);
    const fixedHeightArr = columnsArr.map((item) => item.height);
    const hasFixedHeight = fixedHeightArr.length > 0;
    this.state = {
      x: 0,
      scrollRefLeft: 0,
      scrollRefTop: 0,
      mds: [],
      isDrag: false,
      distanceStyle: "inherit",
      navVisible: true,
      hasFixedHeight,
      fixedHeightArr,
      // 是否固定， 默认为true, 此字段只控制 手动切换
      isFixed: true,
      // isHovered: false
    }
    // this.showCollpase = !!props.scroll && !(isMobile() || props.distance)
    this.scrollRefMouseDown = this.scrollRefMouseDown.bind(this)
    this.scrollRefMouseUp = this.scrollRefMouseUp.bind(this)
    this.scrollRefMouseUps = this.scrollRefMouseUps.bind(this)
    this.antiShake = this.antiShake.bind(this)
    this.updateAutoFillHeight = this.updateAutoFillHeight.bind(this);
    this.initResizeObserver = this.initResizeObserver.bind(this);
    // this.updateCombinationWrapper = this.updateCombinationWrapper.bind(this);
  }


  timer: any = null;
  timmerHandle: any = null;
  get region() {
    let total = 0;
    let regions: number[] = [];
    this.props.columns.forEach(column => {
      total += (+column?.md! ?? 0);
      column?.md && regions.push(+column?.md!)
    })
    return {
      total,
      regions
    }
  }

  antiShake(callBack: Function, wait = 1000, rest?: unknown) {
    let that = this;
    return () => {
      if (that.timer) {
        clearTimeout(that.timer);
        that.timer = null;
      }
      that.timer = setTimeout(() => {
        callBack(rest)
      }, wait)
    }
  }



  renderChild(
    region: string,
    node: SchemaCollection,
    length: number,
    props: any = {}
  ) {
    const { render, itemRender } = this.props;

    return itemRender
      ? itemRender(node, length, this.props)
      : render(region, node, props);
  }

  scrollRefMouseDown(e: MouseEvent) {
    if (this.region.regions?.length > 2) return;
    let initregion: number = this.region.regions[0] / this.region.total / 2;
    if (e.target === this.scrollRefChildren.current) {
      this.timmerHandle = setTimeout(() => {
        this.setState({ isDrag: true })
        this.gridRef.current.onmousemove = (mouse: any) => {
          window.getSelection()?.removeAllRanges()
          let x = mouse.clientX;
          let y = mouse.clientY;
          let mds: string[] = [];
          let leftRegion: number = (x - e.clientX + this.state.scrollRefLeft) / this.gridRef.current?.clientWidth;
          let topRegion: number = (y - e.clientY + this.state.scrollRefTop) / this.gridRef.current?.clientHeight;
          if (this.props?.distance) {
            // 控制可以下拉多大，占据屏幕多大，1为100%
            if (topRegion >= 0.95 || topRegion <= initregion) return;
            mds.push(topRegion * 100 + '%');
            // 控制从表最小多少
            // (topRegion * 100 <= 50) ?
            mds.push((1 - topRegion) * 100 + '%')
            // :
            // mds.push(50 + '%');
          } else {
            if (leftRegion >= 0.5 || leftRegion <= initregion) return;
            mds.push(leftRegion * 100 + '%')
            mds.push((1 - leftRegion) * 100 + '%')
          }
          this.setState({ mds: mds })
        }
      }, 150)
    }
  }

  // 鼠标左键抬起时清除拖动事件，并且将滑块当前位置进行记录
  scrollRefMouseUp(e: MouseEvent) {
    if (this.props.scroll) {
      if (!this.state.isDrag && !this.props?.distance && e.target === this.scrollRefChildren.current) {
        this.gridRef.current.onmousemove = "";
      } else if (this.state.isDrag) {
        document.body.dispatchEvent(new Event(ResizeEvent.DRAGCONTENTRESIZE, { bubbles: true }))
        if (this.props?.distance) {
          this.setState({
            scrollRefTop: this.scrollRef.current.offsetTop,
            isDrag: false
          })
        } else {
          this.setState({
            scrollRefLeft: this.scrollRef.current.offsetLeft,
            isDrag: false
          })
        }
      }
      this.gridRef.current.onmousemove = "";
    }

    clearTimeout(this.timmerHandle);

  }
  lastMds: string[] = [];
  lastScrollLeft: number = 0;
  handleClickCollpase = () => {
    const { navVisible, mds } = this.state;
    if (navVisible) {
      this.lastMds = mds;
      this.lastScrollLeft = this.scrollRef.current?.offsetLeft
      this.setState({
        navVisible: !navVisible,
        scrollRefLeft: 0,
        mds: ["0.1%", "calc(99.9% - 12px)"]
      })
    } else {
      this.setState({
        navVisible: !navVisible,
        scrollRefLeft: this.lastScrollLeft,
        mds: [...this.lastMds]
      });
    }
  }
  // 全局鼠标左键抬起时清除拖动事件
  scrollRefMouseUps(e: MouseEvent) {
    this.gridRef.current.onmousemove = () => { };
  }

  // renderLine() {
  //   const { classnames: cx } = this.props;
  //   return (
  //     <div className={cx('Grid-line')} ref={this.scrollRef} >
  //       <div className={'collpase'}
  //         ref={this.scrollRefChildren}
  //         onMouseEnter={() => this.setState({ isHovered: true })}
  //         onMouseLeave={() => {
  //           if (!this.state.isDrag) {
  //             this.setState({ isHovered: false })
  //           }
  //         }}
  //       >
  //         {(this.state.showLeft === undefined || this.state.showLeft === true) && (
  //           <CaretLeftOutlined
  //             className={`left ${this.state.isHovered ? 'left-hovered' : ''}`}
  //             onClick={(e) => {
  //               e.stopPropagation()
  //               if (this.state.showLeft == undefined) {
  //                 this.setState({ showLeft: false })
  //               } else {
  //                 this.setState({ showLeft: undefined, scrollRefLeft: 21 })
  //               }
  //             }}
  //           />
  //         )}
  //         {(this.state.showLeft === undefined || this.state.showLeft === false) && (
  //           <CaretRightOutlined
  //             className={`right ${this.state.isHovered ? 'right-hovered' : ''}`}
  //             onClick={(e) => {
  //               e.stopPropagation()
  //               if (this.state.showLeft == undefined) {
  //                 this.setState({ showLeft: true })
  //               } else {
  //                 this.setState({ showLeft: undefined, scrollRefLeft: 21 })
  //               }
  //               // 如果容器下有固定到右侧的弹窗容器，才进行操作
  //               const hasPinModal = domUtils.closest(this.gridRef.current as HTMLElement, `.main-page-content.${this.pageContentId}`)?.querySelector('.dialog-pin-right.dialog-fix-right');
  //               if (hasPinModal) {
  //                 const targetEvent = this.state.showLeft == undefined ? ResizeEvent.RESET_MAIN_PAGE_WIDTH + this.pageContentId : ResizeEvent.STOP_RESIZE_AFTER_PIN + this.pageContentId;
  //                 document.body.dispatchEvent(new Event(targetEvent));
  //               }
  //             }}
  //           />
  //         )}
  //       </div>
  //     </div>
  //   )
  // }
  handleToggleAffix(e: HTMLElement) {
    const { isFixed } = this.state;
    const parentDom = domUtils.closest(e.target, '.manual-fix-grid');
    if (parentDom) {
      parentDom.style.position = isFixed ? 'unset' : 'sticky';
    }
    this.setState({
      isFixed: !isFixed
    });
  }
  renderColumn(column: ColumnNode, key: number, length: number) {
    let colProps: {
      [propName: string]: any;
    } = pick(column, ColProps);

    colProps = {
      ...colProps
    };
    const {
      classnames: cx,
      formMode,
      subFormMode,
      subFormHorizontal,
      formHorizontal,
      translate: __,
      scroll,
      className,
      distance,
      classPrefix: ns,
      autoHeight = 0
    } = this.props;

    const {
      mds,
      scrollRefLeft
    } = this.state;

    let style: any = {}
    const statistics = (this.props?.columns?.[0]?.body as Array<any>)[0]?.body?.name
    // 如果高度固定了不设置高度 防止冲突
    if (this.state.hasFixedHeight) {
      const fixedHeightItems = distance ? this.state.fixedHeightArr.filter(Boolean) ?? [] : [];
      // 多减5px 防止出现滚动条
      const resetHeight = (sum(fixedHeightItems) + 5) + 'px';
      if (column.fixedHeight) {
        style.height = column.height ? `${column.height}px` : `calc(${this.state.distanceStyle} - ${resetHeight})`;
      } else {
        style.height = `calc(${this.state.distanceStyle} - ${resetHeight})`
      }
      style.overflowY = 'auto';
    }

    if (scroll && this.region.regions?.length <= 2) {
      if (this.props?.distance) {
        style = mds?.length > 0 ? {
          flex: '0 0 ' + mds[key],
          maxHeight: mds[key],
          height: mds[key],
          maxWidth: + 100 + "%"
        } : {
          flex: '0 0 ' + 50 + "%",
          maxHeight: + 50 + "%",
          height: isMobile() ? '50%' : 'inherit'
        }
      } else {
        style = mds?.length > 0 ? {
          flex: '0 0 ' + mds[key],
          maxWidth: mds[key],
          minWidth: '12px',
        } : {
          flex: '0 0 ' + (this.region.regions[key] / this.region.total * 100 + '%'),
          maxWidth: (this.region.regions[key] / this.region.total * 100 + '%')
        }
      }
    }
    // 如果是垂直模式 且配置了自动分配高度，则将高度设置为自动高度
    if (autoHeight && distance) {
      style = { flex: '0', height: 'auto' };
    }
    if (key === 0 && scroll) {
      return (
        <>
          <div
            key={key}
            style={{
              ...style,
              position: 'relative',
              height: !isMobile() && scroll && !distance && this.gridRef.current?.closest(`.${ns}Modal`) ? 'unset' : (style['height'] ?? '100%'),
            }}
            ref={this.scrollParent}
            className={cx(
              copProps2Class(colProps),
              fromBsClass((column as any).columnClassName!),
              {
                [`Grid-col--v${ucFirst(column.valign)}`]: column.valign,
                'grid-item': className?.includes('grid-item'),
                'mobile-grid-item': isMobile(),
                'Grid-item--combination': !isMobile() && scroll && !distance && !className?.includes('Combination'),
                'scroll-grid-child-item': !isMobile() && scroll && !distance,
                'inline-grid-item': isMobile() && column.mobLayout,
                // 如果有固定项配置，且只配置了一个固定项，则将容器设置为block
                'Grid--block': this.state.hasFixedHeight && this.props.distance,
                // 是否有配置固定
                'grid-item-fixed': Number(column.isFixed ?? 0) > 0,
                // 配置了 用户手动切换固定的容器样式
                'manual-fix-grid': Number(column.isFixed ?? 0) === 2,
              }
            )}
          >
            {Number(column.isFixed ?? 0) === 2 ? (
              <div className={`affix-wrapper ${this.state.isFixed ? 'fixed' : ''}`} onClick={(e) => this.handleToggleAffix(e)}>
                <Icon icon="#icon-tooltool_hold" symbol className="icon" />
              </div>
            ) : null}
            {this.renderChild(`column/${key}`, (column as any).body || '', length, {
              formMode: column.mode || subFormMode || formMode,
              formHorizontal:
                column.horizontal || subFormHorizontal || formHorizontal,
              bodyName: statistics,
              // style: isMobile() && !distance ? { flex: 1 } : {}
            })}

            <span className={cx('Grid--slider', {
              'slider-closed': !this.state.navVisible
            })} ref={this.scrollRef} >
              {this.props?.distance ?
                <Icon icon="up-and-down-arrow" className="icon" /> : null
              }
              <div className={cx('Grid--sliders')} ref={this.scrollRefChildren}></div>
            </span>
            {isMobile() || distance ? null : <div onClick={(e) => {
              e.stopPropagation()
              this.handleClickCollpase()
            }} className={`grid-collpase ${this.state.navVisible ? '' : 'closed'}`}>
              {
                this.state.navVisible ? <span>
                  <DoubleLeftOutlined style={{ fontSize: '11px' }} />收缩
                </span> : <span>
                  <DoubleRightOutlined style={{ fontSize: '11px' }} />展开
                </span>
              }
            </div>}
          </div>
          {/* {this.showCollpase && this.renderLine()} */}
        </>
      )
    }
    return (<div
      key={key}
      style={{ ...style }}
      className={cx(
        copProps2Class(colProps),
        fromBsClass((column as any).columnClassName!),
        {
          [`Grid-col--v${ucFirst(column.valign)}`]: column.valign,
          'grid-item': className?.includes('grid-item'),
          'mobile-grid-item': isMobile(),
          'Grid-item--combination': !isMobile() && scroll && !distance && !className?.includes('Combination'),
          'scroll-grid-child-item': !isMobile() && scroll && !distance,
          'inline-grid-item': isMobile() && column.mobLayout,
          // 如果有固定项配置，且只配置了一个固定项，则将容器设置为block
          'Grid--block': this.state.hasFixedHeight && this.props.distance,
          // 固定grid在顶部
          'grid-item-fixed': Number(column.isFixed ?? 0) > 0,
          // 配置了 用户手动切换固定的容器样式
          'manual-fix-grid': Number(column.isFixed ?? 0) === 2,
        }
      )}
    >
      <div className='grid-relative-wrapper'>
        {Number(column.isFixed ?? 0) === 2 ? (
          <div className={`affix-wrapper ${this.state.isFixed ? 'fixed' : ''}`} onClick={(e) => this.handleToggleAffix(e)}>
            <Icon icon="#icon-tooltool_hold" symbol className="icon" />
          </div>
        ) : null}

        {this.renderChild(`column/${key}`, (column as any).body || '', length, {
          formMode: column.mode || subFormMode || formMode,
          formHorizontal:
            column.horizontal || subFormHorizontal || formHorizontal,
          bodyName: statistics
        })}
      </div>
    </div>)
  }

  renderColumns(columns: ColumnArray) {

    // // 根据md分成的行分布数组
    // const mdMatrix = create2DArrayByMaxSum(this.props.columns.map(_ => +(_?.md || 12)), 12)
    // 计算对应md的行数
    // const IndexToRowKey = (() => {
    //   const result = {}
    //   mdMatrix.map((row, index) => row.map((columns: any, colIndex: any) => { result[index + colIndex] = index + 1 }))
    //   return result
    // })()
    return Array.isArray(columns)
      ? columns.map((column, key) =>
        this.renderColumn({
          ...column,
          // rowKey: IndexToRowKey[key], rowCount: mdMatrix.length,
          fixedHeight: !!(column?.body as any[])?.find?.(_ => _.fixedHeight),
          height: (column.body as any[])?.find?.(_ => _.height)?.['height'],
          isFixed: (column.body as any[])?.find?.(_ => !isNaN(Number(_.isFixed)))?.['isFixed']
        }, key, columns.length)
      )
      : null;
  }

  componentDidMount() {
    const { scroll } = this.props;
    if (scroll) {
      this.setState({
        scrollRefLeft: this.scrollRef.current?.offsetLeft,
        scrollRefTop: this.scrollRef.current.offsetTop
      })
    }
    this.updateAutoFillHeight();
    this.gridRef.current.addEventListener('mousedown', this.scrollRefMouseDown);
    this.gridRef.current.addEventListener('mouseup', this.scrollRefMouseUp);
    document.addEventListener('mouseup', this.scrollRefMouseUps)
    window.addEventListener('resize', this.updateAutoFillHeight);
    // 用于解决AI助手固定右侧的时候显示异常
    const pageContentDom = domUtils.closest(this.gridRef.current as HTMLElement, '.main-page-content');
    const pageContentIdMatch = pageContentDom?.className.split(' ').find((item) => item.includes('page-content-id_')) ?? '';
    this.pageContentId = pageContentIdMatch;
    // 如果子容器有配置固定顶部，则添加父容器的监听器，如果整体高度变化，重新赋值top
    this.initResizeObserver();
    // 如果是pc端的组合页。做一下组合页大屏的特殊处理，让顶层容器的自动分配高度去掉
    // this.updateCombinationWrapper();
  }

  componentWillUnmount() {
    this.gridRef.current.removeEventListener('mousedown', this.scrollRefMouseDown);
    this.gridRef.current.removeEventListener('mouseup', this.scrollRefMouseUp);
    document.removeEventListener('mouseup', this.scrollRefMouseUps)
    window.removeEventListener('resize', this.updateAutoFillHeight);
    this.closeResizeObserver();
  }

  // updateCombinationWrapper() {
  //   if (!isMobile()) {
  //     const gridItems = this.gridRef.current.querySelectorAll('.grid-item');
  //     const { classPrefix: ns } = this.props;
  //     Array.from(gridItems).forEach((item: HTMLElement) => {
  //       const combinationPanel = item.querySelector(`.${ns}Panel.${ns}Panel-Combination`);
  //       const classList = Array.from(combinationPanel?.classList ?? []).join('.');
  //       if (combinationPanel && (!classList.includes('.h-') || classList.includes('.h-full'))) {
  //         const firstGrid = combinationPanel.querySelector(`.${ns}Grid.${ns}Grid-item-search`) as HTMLElement;
  //         if (firstGrid) {
  //           firstGrid.style.flex = '0';
  //         }
  //       }
  //     })
  //   }
  // }

  updateAutoFillHeight() {
    if (this.props.distance) {
      // 获取整个屏幕的高度
      const viewportHeight = isMobile() ? document.body.clientHeight : window.innerHeight;
      // 获取当前元素的头部位置
      const distanceTop = this.gridRef.current.getBoundingClientRect().top

      const formWizard = document.getElementById("form-wizard")?.parentElement as HTMLElement
      // 获取Model
      const Modal = document.getElementsByClassName("amis-dialog-widget")
      let parentNode = this.gridRef.current.parentElement;
      let footerHeight = 0
      let contentBottom = 0
      if (Modal) {
        for (let index = 0; index < Modal.length; index++) {
          if (Modal[index].contains(this.gridRef.current)) {
            Modal[index].childNodes.forEach((item: HTMLElement) => {
              if (item.className.includes("Modal-content")) {
                item.childNodes.forEach((i: HTMLElement) => {
                  if (i.className.includes("Modal-footer")) {
                    footerHeight = footerHeight + i.offsetHeight
                  }
                  if (i.className.includes("Modal-body")) {
                    // 循环获取在model中当前元素所有的祖先元素到Modal-body的内边距和底部border的宽度
                    while (parentNode !== i) {
                      const paddingBottom = getStyleNumber(parentNode, 'padding-bottom');
                      const borderBottom = getStyleNumber(parentNode, 'border-bottom-width');
                      contentBottom = contentBottom + paddingBottom + borderBottom
                      parentNode = parentNode.parentElement;
                    }
                  }
                })
              }
            })
          }
        }
      }
      if (formWizard && formWizard.contains(this.gridRef.current)) {
        formWizard.childNodes.forEach((m: HTMLElement) => {
          if (m.className.includes("Wizard-footer")) {
            footerHeight = +  m.offsetHeight
          }
        })
      }
      // 循环计算父级节点的 padding，这里不考虑父级节点还可能会有其它兄弟节点的情况了
      let allParentPaddingBottom = 0;
      while (parentNode) {
        const paddingBottom = getStyleNumber(parentNode, 'padding-bottom');
        const borderBottom = getStyleNumber(parentNode, 'border-bottom-width');
        allParentPaddingBottom =
          allParentPaddingBottom + paddingBottom + borderBottom;
        parentNode = parentNode.parentElement;
      }
      const finalHeight = viewportHeight - distanceTop - footerHeight - contentBottom - parentNode
      if (finalHeight > 700) {
        this.setState({
          distanceStyle: finalHeight + "px"
        })
      } else {
        this.setState({
          distanceStyle: document.body.clientHeight - 38 + 'px'
        })
      }
    }
  }
  // 如果子容器有配置固定顶部，则添加父容器的监听器，如果整体高度变化，重新赋值top
  initResizeObserver() {
    const { columns = [], classPrefix: ns } = this.props;
    const fixConfigs = columns.map((column) => (column.body as any[])?.find?.(_ => !isNaN(Number(_.isFixed)))?.['isFixed']).filter(Boolean);

    const fixConfig = fixConfigs[0];
    // 如果有配置吸顶
    if (fixConfig) {
      const parentDom = this.gridRef.current;
      const initZIndex = 5;
      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const gridItems = Array.from(entry.target?.querySelectorAll('.grid-item.grid-item-fixed') ?? []);
          const gridHeights = gridItems.map((item: HTMLElement) => item.offsetHeight);
          let height = 0;
          gridItems.forEach((item: HTMLElement, index) => {
            const finalZIndex = initZIndex - index < 1 ? 1 : initZIndex - index;
            height += gridHeights[index - 1] ?? 0;
            item.style.top = height + 'px';
            item.style.zIndex = `${finalZIndex}`;
          });
        })

      });

      resizeObserver.observe(parentDom);
      this.resizeObserver = resizeObserver;
    }

  }
  closeResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.unobserve?.(this.gridRef.current);
    }
  }


  render() {

    const {
      className,
      classnames: cx,
      gap,
      valign: vAlign,
      align: hAlign,
      distance,
      scroll,
      style,
      fullScreen
    } = this.props;
    const { distanceStyle, scrollRefLeft } = this.state;
    // 顶级的得让他滚动
    // const isTopGrid = this.gridRef?.current?.parentElement?.className?.include?.('Page-body')
    // console.log(isTopGrid)
    return (
      <div
        ref={this.gridRef}
        className={cx(
          'Grid',
          {
            [`Grid--${gap}`]: gap,
            [`Grid--v${ucFirst(vAlign)}`]: vAlign,
            [`Grid--h${ucFirst(hAlign)}`]: hAlign,
            'Distance': !this.state.hasFixedHeight && distance,
            'Grid--combination': !isMobile() && scroll && !distance && !className?.includes('Combination'),
            'Grid-item-search': scrollRefLeft > 20 ? false : true,
            'scroll-grid-item': !isMobile() && scroll && !distance
          },
          className
        )}
        // Jay
        style={{ ...style, maxHeight: distanceStyle, height: distanceStyle, overflow: '' }}
      >
        {this.renderColumns(this.props.columns)}
      </div>
    );
  }
}

@Renderer({
  type: 'grid'
})
export class GridRenderer extends Grid<{}> { }
